/**
 * Connections API - Multi-Mode Connection System
 * 
 * API wrapper for /api/connections/* endpoints
 * Supports: dating, friendship, business, community modes
 */

import apiClient from "./client";

function extractAxiosErrorMessage(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;
  return (
    data?.detail ||
    data?.message ||
    (typeof data === "string" ? data : null) ||
    (status ? `Request failed (${status})` : null) ||
    e?.message ||
    "Request failed"
  );
}

async function get(path, config) {
  try {
    const res = await apiClient.get(path, config);
    return res.data;
  } catch (e) {
    throw new Error(extractAxiosErrorMessage(e));
  }
}

async function post(path, body, config) {
  try {
    const res = await apiClient.post(path, body, config);
    return res.data;
  } catch (e) {
    throw new Error(extractAxiosErrorMessage(e));
  }
}

export const ConnectionsAPI = {
  /**
   * Get current user's connection preferences
   */
  getPrefs: () => get("/connections/me/prefs"),
  
  /**
   * Set connection preferences
   * @param {Object} payload - { connection_mode, visibility_scope, discoverable, discoverable_modes }
   */
  setPrefs: (payload) => post("/connections/me/prefs", payload),

  /**
   * List all connections, requests, and blocked users
   * Returns: { incoming_requests, outgoing_requests, connections, blocked }
   */
  listMine: () => get("/connections/me/list"),

  /**
   * Send a connection request
   * @param {Object} payload - { other_user_id, intent? }
   */
  sendRequest: (payload) => post("/connections/request", payload),
  
  /**
   * Respond to a connection request
   * @param {string} edgeId - The connection edge ID
   * @param {Object} payload - { accept: boolean }
   */
  respondRequest: (edgeId, payload) =>
    post(`/connections/request/${edgeId}/respond`, payload),

  /**
   * Block a user
   * @param {Object} payload - { other_user_id }
   */
  blockUser: (payload) => post("/connections/block", payload),
  
  /**
   * Unblock a user
   * @param {Object} payload - { other_user_id }
   */
  unblockUser: (payload) => post("/connections/unblock", payload),

  // ==========================================================================
  // CONNECTION AWARENESS ("Who's Here")
  // ==========================================================================
  
  /**
   * Get counts of discoverable users by mode
   * Returns: { counts: { dating, friendship, business, community } }
   */
  awarenessCounts: () => get("/connections/awareness/counts"),
  
  /**
   * Get a batch of suggestions for a specific mode
   * @param {string} mode - dating|friendship|business|community
   * @param {number} limit - Max suggestions (1-10)
   * Returns: { mode, suggestions: [{ id, display_name, avatar_url, ... }] }
   */
  awarenessSuggestions: (mode, limit = 5) =>
    get(`/connections/awareness/suggestions?mode=${encodeURIComponent(mode)}&limit=${limit}`),
  
  /**
   * Hide a user from suggestions (soft-hide, not block)
   * @param {string} other_user_id - User ID to hide
   */
  awarenessHide: (other_user_id) =>
    post("/connections/awareness/hide", { other_user_id }),
  
  /**
   * Unhide a user from suggestions
   * @param {string} other_user_id - User ID to unhide
   */
  awarenessUnhide: (other_user_id) =>
    post("/connections/awareness/unhide", { other_user_id }),

  // ==========================================================================
  // INVITE LINKS (Social Trust-Chain - NOT revenue referrals)
  // ==========================================================================

  /**
   * Resolve an invite code (public - no auth required)
   * @param {string} code - Invite code
   * Returns: { ok, code, mode, note, inviter_user_id }
   */
  resolveInvite: (code) =>
    get(`/connections/invites/resolve/${encodeURIComponent(code)}`),

  /**
   * Consume an invite after login (stamps user with social context)
   * @param {string} code - Invite code
   * Returns: { ok, invited_by_user_id, mode } or { ok, already_set }
   */
  consumeInvite: (code) =>
    post("/connections/invites/consume", { code }),

  /**
   * Create an invite link
   * @param {Object} params - { mode, ttl_hours, max_uses, note }
   * Returns: { ok, code, mode, expires_at }
   */
  createInvite: ({ mode = "friendship", ttl_hours = 168, max_uses = null, note = "" } = {}) =>
    post("/connections/invites/create", { mode, ttl_hours, max_uses, note }),

  /**
   * List user's created invites
   * @param {number} limit - Max invites to return (1-100)
   * Returns: { invites: [...] }
   */
  listMyInvites: (limit = 20) =>
    get(`/connections/invites/my?limit=${limit}`),
};

export default ConnectionsAPI;
